import React, {useMemo} from 'react';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import useSelectorsById, {selectorsAreLoaded} from 'notebook/structured-tables/hooks/useSelectorsById';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ProcedureSchemaFieldTypeTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {hasArchivedModelSchema} from 'procedures/design/utils/equipmentSchemaHelpers';
import {ProcedureSchemaFieldTypeContext} from 'procedures/tables/ProcedureTableConstants';
import {
  doesSchemaImplementEquipmentInterface,
  useAllTagSchemasWithProceduresShape,
  useAllTagSchemasWithStandardShape,
} from 'procedures/utils/useAllTagSchemas';
import {SchemaInterface} from 'schemas/SchemaConstants';
import {useAllSchemaInterfacesQuery} from 'schemas/util/useAllSchemaInterfacesQuery';
import {getTagSchemaSummaryGraphQLFromListingWithFields} from 'tag-schema/util/TagSchemaUtil';
import {CenteredSpinner} from 'ui-library/Spinner';

import {TagSchemaListingWithFieldsGraphQL} from '../../../../graphql-fragments/schemas/__generated/TagSchemaListingWithFieldsGraphQL';
import {
  DEFAULT_DATA_PARAMETER_TYPE,
  DEFAULT_MATERIAL_TYPE,
  MaterialType,
} from './procedureSchemaFieldConstants';
import {
  convertDataParameterTypeToTypedValue,
  convertMaterialTypeToTypedValue,
  convertTypedValueToDataParameterType,
  convertTypedValueToMaterialType,
  doesTypedValueHaveLinkedSchemaInterface,
} from './procedureSchemaFieldTypeConversionUtils';
import {
  ProcedureDataParameterTypeSelector,
  ProcedureMaterialTypeSelector,
} from './ProcedureSchemaFieldTypeSelectors';

interface ProcedureSchemaFieldTypeSelectorForTablesProps {
  staticDisplay?: boolean;
  typedValue: TypedValue | null;
  onUpdate: (tv: TypedValue) => void;
  context: ProcedureSchemaFieldTypeContext;
}

/**
 * @package Exported for testing
 */
export function ProcedureMaterialTypeSelectorForTables(
  props: ProcedureSchemaFieldTypeSelectorForTablesProps
): JSX.Element {
  const tagSchemas = useAllTagSchemasWithStandardShape();
  const tagSchemasForProcedures = useAllTagSchemasWithProceduresShape();
  const schemaInterfacesResult = useAllSchemaInterfacesQuery();
  const areSchemasStillLoading = tagSchemas == null || tagSchemasForProcedures == null;

  // null (not []) while the query is pending, so hydration can distinguish "still loading" from
  // "genuinely absent" and avoid wrongly firing a "could not find fieldset" error during load.
  const schemaInterfaces: Array<SchemaInterface> | null = useMemo(
    () =>
      schemaInterfacesResult.type === QueryResultType.SUCCESS
        ? schemaInterfacesResult.data.schemaInterfaces
        : null,
    [schemaInterfacesResult]
  );
  const nonArchivedSchemaInterfaces = useMemo(
    () => (schemaInterfaces ?? []).filter((schemaInterface) => !isArchived(schemaInterface)),
    [schemaInterfaces]
  );

  const tagSchemasForDropdown = useMemo(() => {
    if (areSchemasStillLoading) {
      return null;
    }
    return tagSchemas
      .filter((ts) => !isArchived(ts))
      .filter((ts) => {
        const tsForProcedure = tagSchemasForProcedures.find((tsp) => tsp.id === ts.id);
        assert(tsForProcedure != null, 'Querying all schemas should always return the same set of schemas');
        const isEquipmentSchema = doesSchemaImplementEquipmentInterface(tsForProcedure);

        if (props.context === ProcedureSchemaFieldTypeContext.EQUIPMENT) {
          if (!isEquipmentSchema) {
            return false;
          }
          // Exclude equipment schemas whose linked Model field points to an archived schema
          return !hasArchivedModelSchema(ts);
        }
        if (
          [
            ProcedureSchemaFieldTypeContext.MATERIAL_INPUT,
            ProcedureSchemaFieldTypeContext.MATERIAL_OUTPUT,
          ].includes(props.context)
        ) {
          return !isEquipmentSchema; // Cannot use equipment as materials
        }

        return true;
      });
  }, [tagSchemas, tagSchemasForProcedures, areSchemasStillLoading, props.context]);

  if (areSchemasStillLoading) {
    return <CenteredSpinner />;
  }

  const getSelectedMaterialType = (
    tagSchemas: Array<TagSchemaListingWithFieldsGraphQL>
  ): MaterialType | null => {
    let tv: ProcedureSchemaFieldTypeTypedValue | null;
    const isOutputContext = props.context === ProcedureSchemaFieldTypeContext.MATERIAL_OUTPUT;
    if (props.typedValue?.type === BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE) {
      const fd = props.typedValue.fieldDefinition;
      const isUnconstrainedOutput =
        isOutputContext &&
        fd.linkedTagSchemaId == null &&
        fd.linkedFolderItemType == null &&
        !doesTypedValueHaveLinkedSchemaInterface(props.typedValue);
      tv = isUnconstrainedOutput ? null : props.typedValue;
    } else if (isOutputContext) {
      tv = null;
    } else {
      tv = convertMaterialTypeToTypedValue(DEFAULT_MATERIAL_TYPE);
    }
    return tv ? convertTypedValueToMaterialType(tv, tagSchemas, schemaInterfaces) : null;
  };

  return (
    <ProcedureMaterialTypeSelector
      staticDisplay={props.staticDisplay}
      selectedType={getSelectedMaterialType(tagSchemas)}
      onChangeSelection={(materialType) => {
        props.onUpdate(convertMaterialTypeToTypedValue(materialType));
      }}
      tagSchemas={
        tagSchemasForDropdown?.map((ts) => getTagSchemaSummaryGraphQLFromListingWithFields(ts)) ?? []
      }
      schemaInterfaces={nonArchivedSchemaInterfaces}
      context={props.context}
    />
  );
}

function ProcedureDataParameterTypeSelectorForTables(
  props: ProcedureSchemaFieldTypeSelectorForTablesProps
): JSX.Element {
  const selectorsById = useSelectorsById({includeArchivedSelectors: false});
  const tv: ProcedureSchemaFieldTypeTypedValue =
    props.typedValue?.type === BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE
      ? props.typedValue
      : convertDataParameterTypeToTypedValue(DEFAULT_DATA_PARAMETER_TYPE);
  const selectorsLoaded = selectorsAreLoaded(selectorsById);

  // Intentionally leave the type selector enabled while selectors are still loading or errored so the
  // user can still pick the non-dropdown types (text, decimal, datetime). The Dropdown option is only
  // built from loaded selectors (see getFieldOptions), so it simply won't appear until they resolve.
  return (
    <ProcedureDataParameterTypeSelector
      staticDisplay={props.staticDisplay}
      selectedType={convertTypedValueToDataParameterType(tv)}
      schemaFieldSelectors={selectorsLoaded ? Object.values(selectorsById) : []}
      onChangeSelection={(dataParameterType) => {
        props.onUpdate(convertDataParameterTypeToTypedValue(dataParameterType));
      }}
    />
  );
}

export default function ProcedureSchemaFieldTypeSelectorForTables(
  props: ProcedureSchemaFieldTypeSelectorForTablesProps
): JSX.Element | null {
  switch (props.context) {
    case ProcedureSchemaFieldTypeContext.MATERIAL_INPUT:
    case ProcedureSchemaFieldTypeContext.MATERIAL_OUTPUT:
    case ProcedureSchemaFieldTypeContext.EQUIPMENT:
      return <ProcedureMaterialTypeSelectorForTables {...props} />;

    case ProcedureSchemaFieldTypeContext.DATA_PARAMETER:
      return <ProcedureDataParameterTypeSelectorForTables {...props} />;

    default:
      return null;
  }
}
